/**
 * The business, in aggregate.
 *
 * EVERY WINDOW IS LABELLED ON THE FIGURE ITSELF, not once in a heading. The
 * report mixes rolling 30-day windows with UTC calendar months (the fee block
 * follows the month-end debit job, not the clock), and a number whose window an
 * operator has to infer from where it sits on the page is a number they will
 * eventually compare against the wrong thing.
 *
 * `conversionRatePct` EXCLUDES pending and held attributions from its
 * denominator — they are still in flight, and counting them as failures makes a
 * healthy referral program look broken on the day it launches. That is said on
 * screen rather than left in the contract, because the one person who checks
 * this number is the one who will act on it.
 *
 * THE `matchCredit` BLOCK IS v0.9 VOCABULARY THAT v0.11 RETIRED, and the `v09`
 * block is optional in the contract. Both are rendered only when they carry
 * something — a wall of zeroes under headings no current operator recognises
 * teaches them to skim the page, which is the opposite of what a reports screen
 * is for.
 */
import { useAdminReports } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCents } from '@/utils/money';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold tabular-nums text-white/90">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] leading-relaxed text-white/35">{sub}</div>}
    </div>
  );
}

const GRID = 'grid gap-2 sm:grid-cols-2 lg:grid-cols-4';

export function ReportsPage() {
  const query = useAdminReports();

  if (query.isLoading) return <AdminLoading label="Aggregating" />;
  if (query.isError || !query.data)
    return <AdminError error={query.error} onRetry={() => void query.refetch()} />;

  const r = query.data;

  const legacyCredit =
    r.matchCredit.outstandingCents !== 0 ||
    r.matchCredit.issuedLast30dCents !== 0 ||
    r.matchCredit.spentLast30dCents !== 0 ||
    r.matchCredit.wonLast30dCents !== 0 ||
    r.matchCredit.voidedLast30dCents !== 0 ||
    r.matchCredit.adjustmentNetLast30dCents !== 0;

  return (
    <>
      <AdminPageHeader
        title="Reports"
        subtitle={`Generated ${new Date(r.generatedAt).toLocaleString('en-AU')}. Each figure carries its own window — rolling 30 days and UTC calendar months are both in use here.`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        }
      />

      <div className="space-y-5">
        <Card title="Signups">
          <div className={GRID}>
            <Stat label="Today" value={r.signups.today} sub="UTC day" />
            <Stat label="Last 7 days" value={r.signups.last7d} sub="rolling" />
            <Stat label="Last 30 days" value={r.signups.last30d} sub="rolling" />
            <Stat label="All time" value={r.signups.allTime} />
          </div>
        </Card>

        <Card title="Identity verification">
          <div className={GRID}>
            <Stat label="Unverified" value={r.kycDistribution.unverified} />
            <Stat label="Pending" value={r.kycDistribution.pending} />
            <Stat label="Verified" value={r.kycDistribution.verified} />
            <Stat label="Rejected" value={r.kycDistribution.rejected} />
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/35">
            Counts of accounts, not of verification attempts. Verification is required before a
            first deposit, so a large unverified bucket is signups who have not funded yet — not
            a backlog.
          </p>
        </Card>

        <Card title="Money in and out">
          <div className={GRID}>
            <Stat
              label="Deposits · 30d"
              value={formatCents(r.deposits.last30d.volumeCents)}
              sub={`${r.deposits.last30d.count} deposit${r.deposits.last30d.count === 1 ? '' : 's'}`}
            />
            <Stat
              label="Deposits · all time"
              value={formatCents(r.deposits.allTime.volumeCents)}
              sub={`${r.deposits.allTime.count} deposit${r.deposits.allTime.count === 1 ? '' : 's'}`}
            />
            <Stat
              label="Withdrawals · 30d"
              value={formatCents(r.withdrawals.last30d.volumeCents)}
              sub={`${r.withdrawals.last30d.count} withdrawal${r.withdrawals.last30d.count === 1 ? '' : 's'}`}
            />
            <Stat
              label="Withdrawals · all time"
              value={formatCents(r.withdrawals.allTime.volumeCents)}
              sub={`${r.withdrawals.allTime.count} withdrawal${r.withdrawals.allTime.count === 1 ? '' : 's'}`}
            />
          </div>
        </Card>

        <Card title="Transaction fee">
          <div className={GRID}>
            <Stat
              label="Rate"
              value={`${(r.transactionFee.rateBps / 100).toFixed(2)}%`}
              sub={`${r.transactionFee.rateBps} bps`}
            />
            <Stat
              label="Fees charged"
              value={formatCents(r.transactionFee.feesChargedThisMonthCents)}
              sub={`${r.transactionFee.currentBillingMonth} (UTC month)`}
            />
            <Stat
              label="Turnover"
              value={formatCents(r.transactionFee.turnoverThisMonthCents)}
              sub={`${r.transactionFee.currentBillingMonth} (UTC month)`}
            />
            <Stat
              label="Fees charged · all time"
              value={formatCents(r.transactionFee.feesChargedAllTimeCents)}
            />
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/35">
            Fees ACTUALLY CHARGED, summed from the bet rows — not an accrual estimate. The fee is
            charged on top of the stake at placement and is never refunded, so this figure only
            moves when a bet is placed.
          </p>
        </Card>

        <Card title="Referrals">
          <div className={GRID}>
            <Stat label="Converted" value={r.referrals.converted} />
            <Stat label="Pending" value={r.referrals.pending} sub="still in flight" />
            <Stat label="Held" value={r.referrals.held} sub="fraud guard — needs a decision" />
            <Stat
              label="Conversion rate"
              value={`${r.referrals.conversionRatePct.toFixed(1)}%`}
              sub="excludes pending + held"
            />
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Rejected" value={r.referrals.rejected} />
            <Stat label="Expired" value={r.referrals.expired} />
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/35">
            The rate is converted ÷ (converted + rejected + expired). Pending and held
            attributions are deliberately out of the denominator — they have not failed, they
            have not finished. A rising <span className="text-white/60">held</span> count is the
            one to act on: each is an attribution the fraud guard stopped and nobody has ruled on.
          </p>
        </Card>

        {legacyCredit && (
          <Card title="Legacy credit — retired in v0.11, shown because the ledger still carries it">
            <div className={GRID}>
              <Stat label="Outstanding" value={formatCents(r.matchCredit.outstandingCents)} />
              <Stat label="Issued · 30d" value={formatCents(r.matchCredit.issuedLast30dCents)} />
              <Stat label="Spent · 30d" value={formatCents(r.matchCredit.spentLast30dCents)} />
              <Stat label="Won · 30d" value={formatCents(r.matchCredit.wonLast30dCents)} />
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Voided · 30d" value={formatCents(r.matchCredit.voidedLast30dCents)} />
              <Stat
                label="Adjustments · 30d"
                value={formatCents(r.matchCredit.adjustmentNetLast30dCents)}
                sub="signed net of staff adjustments"
              />
            </div>
          </Card>
        )}

        {r.v09 && (
          <Card title="Savings pool">
            <div className={GRID}>
              <Stat label="Total savings (locked)" value={formatCents(r.v09.totalSavingsCents)} />
              <Stat label="Total play balance" value={formatCents(r.v09.totalBalanceCents)} />
              <Stat
                label="Fee arrears"
                value={formatCents(r.v09.totalArrearsCents)}
                sub="fee debt that could not be debited"
              />
              <Stat
                label="Phantom excess"
                value={formatCents(r.v09.totalPhantomExcessCents)}
                sub="audit counter — only ever rises"
              />
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Locked-savings state"
                value={r.v09.usersInLockedSavingsState}
                sub="cap exceeds play balance by >$1"
              />
              <Stat label="Bets · balance · 30d" value={r.v09.betsBySourceLast30d.balance} />
              <Stat label="Bets · credit · 30d" value={r.v09.betsBySourceLast30d.bc} />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-white/35">
              A high locked-savings count means the customer-facing “locked in Savings” surface is
              doing load-bearing work — those accounts can withdraw more than their play balance
              shows, eventually, and will ask why they cannot do it today.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
